import type {
  StoreLowestDeliveryPriceFragment,
  StoreLowestDeliveryPricesFragment,
} from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { EuroCents } from '@shared/domain/euro-cents'
import { DeliveryMethod } from '@shared/gql/document-nodes'
import {
  DeliveryOutline24,
  HomeDeliveryOutline24,
  LocationMapOutline24,
  StoreOutline24,
} from '@sok/design-system'
import { Icon } from 'components/icons'
import { NextLink } from 'components/NextLink'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { getDeliveryMethodString } from 'utils/delivery/get-delivery-method-string'

import { StartShoppingButton } from './StartShoppingButton'
import {
  StoreInfoCard,
  StoreInfoCardItem,
  StoreInfoCardItemWrapper,
  StoreInfoCardTitle,
} from './StoreInfoCard'

const buttonIcon = (deliveryMethod: DeliveryMethod | null | undefined, isFastTrack: boolean) => {
  if (deliveryMethod === DeliveryMethod.HomeDelivery) {
    return isFastTrack ? <HomeDeliveryOutline24 /> : <DeliveryOutline24 />
  } else if (deliveryMethod === DeliveryMethod.Pickup) {
    return isFastTrack ? (
      <Icon name="fast-track-pickup" width={32} height={24} margin="0 0 0 -6px " />
    ) : (
      <StoreOutline24 />
    )
  } else {
    return <LocationMapOutline24 />
  }
}

const StoreDeliveryArea: FC<{
  area: Pick<StoreLowestDeliveryPriceFragment, 'deliveryMethod' | 'isFastTrack' | 'price'>
}> = ({ area }) => {
  const { t } = useTranslation()

  return (
    <StoreInfoCardItem data-test-id="store-delivery-area">
      {buttonIcon(area.deliveryMethod, area.isFastTrack)}
      <span>
        {t(getDeliveryMethodString(area.deliveryMethod, area.isFastTrack))}{' '}
        {t('Price starting from', { price: EuroCents.fromEuros(area.price).format() })}
      </span>
    </StoreInfoCardItem>
  )
}

export const StoreOnlineStore: FC<{
  showStoreLink: boolean
  store: StoreLowestDeliveryPricesFragment
}> = ({ showStoreLink, store }) => {
  const { t } = useTranslation()

  if (!store.lowestDeliveryPrices) {
    return null
  }

  return (
    <StoreInfoCard>
      <StoreInfoCardTitle>{t('Online store')}</StoreInfoCardTitle>

      <StoreInfoCardItemWrapper>
        <p>
          <span>
            {t('Online store description', { context: showStoreLink ? null : 'noSkaupat' })}{' '}
          </span>
          <NextLink
            href="https://www.s-kaupat.fi/sivu/uusi-ruoan-verkkokauppa"
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('Read more about us')}
          </NextLink>
          <span> {t('Online store description_noSkaupat continued')}</span>
        </p>
        {store.lowestDeliveryPrices.map(
          (area) => area && <StoreDeliveryArea area={area} key={area.areaId} />,
        )}
        {showStoreLink && <StartShoppingButton />}
      </StoreInfoCardItemWrapper>
    </StoreInfoCard>
  )
}
